import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { Alert, AlertService, AlertType, PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CasingCreditFiltersResponse, Pagination } from '../../models/response.model';
import { map } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CasingCredit } from '../../models/casing-credit.model';
import { Router } from '@angular/router';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { MatDialog } from '@angular/material/dialog';
import { SharedDialogComponent } from '../../../../shared/components/shared-dialog/shared-dialog.component';
import { CustomerAssignService } from '../../../../shared/services/customer-assign.service';

@Component({
  selector: 'app-casing-credit',
  templateUrl: './casing-credit.component.html',
  styleUrl: './casing-credit.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingCreditComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingCredit>;
  codesList: string[] = [];
  descriptionList: string[] = [];
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public dialog = inject(MatDialog);
  public customerAssignService = inject(CustomerAssignService);
  public cdr = inject(ChangeDetectorRef);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  casingCredits: CasingCredit[] = [];
  plants: string[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  pagination: Pagination;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  currentCasingCredit: CasingCredit;
  productSaveDisabled = true;
  casingCreditBreadcrumbs = [
    { link: '/home/dashboard', icon: 'home' },
    { label: this.translationService.translate('productDataText') },
    { link: '/home/productData/casingCredit', label: this.translationService.translate('casingCreditText') }
  ];
  additionalColumns = [
    { property: 'casingCreditCode', headerKey: this.translationService.translate('casingCreditCodeText') },
    { property: 'casingCreditDesc', headerKey: this.translationService.translate('casingCreditDescText') }
  ];
  tableConfig: TableConfig<CasingCredit> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {},
    onRowClick: (elm: CasingCredit) => {
      this.router.navigateByUrl(`/home/productData/casingCredit/casingcreditdetails/${elm.casingCreditPk}`, {
        state: { code: elm.creditCode, description: elm.description }
      });
    }
  };

  fetcher: PaginatorFetcher<CasingCredit> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    return this.productDataService.getCasingCredits({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        this.casingCredits = response.data.casingCredits || [];
        this.pagination = response.data.pagination;
        this.updateDisplayedRowsForm(this.casingCredits);
        return { items: this.casingCredits, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      code: [[]],
      description: [[]]
    });
    this.filterFormBackend = this.fb.group({
      code: [[]],
      description: [[]]
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.productDataService.getCasingCreditFilters().subscribe((response: CasingCreditFiltersResponse) => {
      this.codesList = response.data.casingCodes || [];
      this.descriptionList = response.data.creditDescriptions || [];
    });
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        creditCode: { cell: (row) => row.creditCode, header: this.translationService.translate('codeText') },
        description: { cell: (row) => row.description, header: this.translationService.translate('descriptionText') }
      };
      this.cdr.markForCheck();
    });
  }

  assignToCustomer(item: any) {
    this.currentCasingCredit = item;
    const navigationData = {
      breadcrumbLinks: this.casingCreditBreadcrumbs,
      currentDataObject: this.currentCasingCredit,
      additionalColumns: this.additionalColumns,
      searchUri: 'searchCasingCreditAssignToCustomer',
      assignUri: 'assignCustomerCasingCredit',
      service: 'backOfficeUrl'
    };

    this.customerAssignService.setNavigationData(navigationData);
    this.router.navigate(['/home/productData/customer-assign', this.currentCasingCredit.casingCreditPk]);
  }

  deleteCasingCredit(item: CasingCredit) {
    const dialogRef = this.dialog.open(SharedDialogComponent, {
      width: '550px',
      data: {
        title: this.translationService.translate('warningText'),
        message: this.translationService.translate('deleteCasingCreditConfirmText'),
        buttonText: this.translationService.translate('confirmText'),
        cancelButtonText: this.translationService.translate('cancelText')
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Call API service to delete the record
        this.productDataService.deleteCasingCredit(item.casingCreditPk).subscribe({
          next: () => {
            this.alertService.showAlert({
              content: this.translationService.translate('casingCreditDeletedSuccessfully'),
              type: AlertType.success
            } as Alert);
            // Refresh data after successful deletion
            this.tableRef.resetFilters();
          },
          error: (error) => {
            this.alertService.showAlert({
              content: error?.error?.message || this.translationService.translate('errorOccurred'),
              type: AlertType.critical
            } as Alert);
          }
        });
      }
    });
  }

  copyCasingCredit(item: CasingCredit) {
    this.router.navigate(['/home/productData/casingCredit/copyCasingCredit', item.casingCreditPk]);
  }

  createRowForm(row: CasingCredit): FormGroup {
    return this.fb.group({
      casingCreditPk: [row.casingCreditPk],
      creditCode: [row.creditCode],
      description: [row.description]
    });
  }

  updateDisplayedRowsForm(rows: CasingCredit[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  getControl(item: CasingCredit, controlName: keyof CasingCredit): FormControl {
    const index = this.casingCredits.findIndex((row) => row.creditCode === item.creditCode);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  clearFilters(): void {
    this.filterForm.reset();
  }

  createCasingCredit(): void {
    this.router.navigateByUrl(`/home/productData/casingCredit/createCasingCreditDetails`);
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      code: this.filterForm.get('code')?.value || '',
      description: this.filterForm.get('description')?.value || ''
    });
  }
  protected isDeletable(item: CasingCredit): boolean {
    const roles: string = localStorage.getItem('roles') || '';
    return !item.deletable && roles.includes('PRODUCT_SETUP_EDIT');
  }
  protected readonly localStorage = localStorage;
  protected readonly FEATURE_FLAGS = FEATURE_FLAGS;
}
